import React, { useState, useEffect, useMemo, useRef } from "react";
import styled, { css } from "styled-components";
import { Player, GameRole } from "../../types";
import { dictionaries } from "../../utils/dictionary";
import GameActions from "./GameActions";
import PlayerPunishment from "./PlayerPunishment";
import Voting from "./Voting";
import LastWords from "./LastWords";
import MorningTransition from "./MorningTransition";
import PhaseHeader from "./PhaseHeader";

interface DayPhaseProps {
  language: number;
  players: Player[];
  playerRoles: GameRole[];
  judge?: Player;
  playersLeaved: number[];
  playersLeavingNow?: number[]; // Players currently saying farewell (still shown in list)
  playersLeavedGP: number[];
  playersLeavedPenalty: number[];
  playerFauls: number[];
  penaltyListToday: number[];
  firstTalk: number;
  nightCount: number;
  handledFouls: Set<string>;
  nominations: Record<string, string>;
  streamLink?: string;
  eliminatedPlayers?: Player[];
  eliminatedPlayerNumbers?: number[];
  eliminationType?: "voted" | "killed";
  votingInProgress?: boolean;
  streamError?: boolean;
  morningTransition?: {
    eliminatedPlayer?: number;
    isFirstNight: boolean;
    firstDayEliminations: number;
    onBestMove: (guesses: number[]) => void;
    onLastWords: () => void;
    onContinue: () => void;
    onUndo?: () => void;
  };
  getOrderedPlayersFromFirstTalk: (
    firstTalkIndex: number,
    playersLeaved: number[]
  ) => number[];
  onNominationChange: (playerIndex: number, nomination: string) => void;
  onSaveDayState: () => void;
  onVotingStart: (nominations: Record<string, string>) => void;
  onVotingComplete?: (results: any) => void;
  onSetVotingInProgress?: (inProgress: boolean) => void;
  onGoToNight: () => void;
  onPlayerPunish: (playerIndex: number) => void;
  onPlayerFoulAction: (
    playerIndex: number,
    action:
      | "add"
      | "reset"
      | "disqualify"
      | "penalty-today"
      | "penalty-tomorrow"
  ) => void;
  onMarkFoulAsHandled: (playerIndex: number, foulCount: number) => void;
  onDisqualifyPlayer: (playerIndex: number) => void;
  onPPK: (playerIndex: number, winningTeam: "red" | "black") => void;
  onLastWordsComplete?: () => void;
  onLastWordsUndo?: () => void;
  onLanguageChange?: (language: number) => void;
  onStreamSettingsChange?: (theme: string, language: number) => void;
  onTimeoutToggle: () => void;
  onTechnicalBreak?: () => void;
  onTechnicalBreakEnd?: () => void;
  onCancel: () => void;
  // Timer state
  timerAccumulated?: number;
  timerStartedAt?: number;
  onTimerStateChange?: (accumulated: number, startedAt: number | undefined) => void;
}

const PhaseBackground = styled.div`
  position: relative;
  min-height: 100vh;
  padding-bottom: 4rem;
  background: radial-gradient(
      circle at top,
      rgba(255, 200, 93, 0.2),
      transparent 55%
    ),
    radial-gradient(
      circle at bottom right,
      rgba(124, 93, 255, 0.32),
      transparent 60%
    ),
    #0f0b1d;
  color: #f5f2ff;
`;

const PhaseContainer = styled.div`
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem 2rem 3rem;

  @media (max-width: 992px) {
    padding: 1.5rem 1.5rem 2.5rem;
  }

  @media (max-width: 768px) {
    padding: 0 0.75rem 2rem;
  }
`;

const PhaseMain = styled.div.attrs({ className: "main" })`
  margin-top: 1rem;

  @media (max-width: 768px) {
    margin-top: 0.5rem !important;
  }
`;

const PhaseStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (max-width: 768px) {
    gap: 1.5rem;
  }
`;

const SectionCard = styled.div`
  padding: 1rem 1.25rem;
  border-radius: 22px;
  background: rgba(18, 12, 36, 0.94);
  border: 1px solid rgba(124, 93, 255, 0.22);
  box-shadow: 0 16px 48px rgba(10, 6, 25, 0.35);

  @media (max-width: 768px) {
    padding: 1.35rem 1.5rem;
  }
`;

const InstructionCard = styled(SectionCard)`
  display: none;
  background: rgba(255, 196, 0, 0.12);
  border: 1px solid rgba(255, 196, 0, 0.38);
  color: #ffe9a4;
  box-shadow: 0 12px 32px rgba(255, 196, 0, 0.18);

  ul {
    margin: 0.75rem 0 0;
    padding-left: 1.25rem;
  }

  li + li {
    margin-top: 0.35rem;
  }
`;

const NominationSummaryCard = styled(SectionCard)`
  background: rgba(68, 255, 210, 0.12);
  border: 1px solid rgba(68, 255, 210, 0.32);
  color: #c8fff0;
  box-shadow: 0 12px 32px rgba(68, 255, 210, 0.18);

  ul {
    margin: 0.75rem 0 0;
    padding-left: 1.25rem;
  }

  li + li {
    margin-top: 0.35rem;
  }
`;

const VotingSectionCard = styled(SectionCard)`
  scroll-margin-top: 140px;

  @media (max-width: 992px) {
    scroll-margin-top: 120px;
  }

  @media (max-width: 768px) {
    scroll-margin-top: 88px;
  }
`;

const MetaCard = styled(SectionCard)`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: rgba(245, 242, 255, 0.75);
`;

const PlayersTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: rgba(18, 12, 36, 0.94);
  border-radius: 22px;
  overflow: hidden;
  box-shadow: 0 16px 48px rgba(10, 6, 25, 0.35);

  thead th {
    padding: 0.75rem 1rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-size: 0.7rem;
    font-weight: 700;
    background: rgba(255, 255, 255, 0.08);
    color: rgba(245, 242, 255, 0.82);

    @media (max-width: 768px) {
      padding: 0.75rem 0.5rem;
      font-size: 0.65rem;
    }
  }

  tbody tr {
    transition: background 0.2s ease, transform 0.2s ease;
  }

  tbody tr:nth-child(even) {
    background: rgba(255, 255, 255, 0.02);
  }

  tbody td {
    padding: 0.65rem 1rem;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    vertical-align: middle;

    @media (max-width: 768px) {
      padding: 0.6rem 0.5rem;
    }
  }
`;

const PlayerRow = styled.tr`
  &.talk {
    background: linear-gradient(
      135deg,
      rgba(68, 255, 210, 0.08),
      rgba(124, 93, 255, 0.18)
    );
    box-shadow: 0 4px 12px rgba(68, 255, 210, 0.15);
  }

  &.penalty {
    background: rgba(255, 88, 108, 0.12);
  }

  &.penaltytalk {
    background: linear-gradient(
      135deg,
      rgba(255, 88, 108, 0.18),
      rgba(124, 93, 255, 0.18)
    );
    box-shadow: 0 4px 12px rgba(255, 88, 108, 0.15);
  }

  &.dead {
    opacity: 0.35;
  }
`;

const NumberCell = styled.td`
  width: 56px;
  font-weight: 700;
  font-size: 0.95rem;

  @media (max-width: 768px) {
    width: 40px;
    font-size: 0.85rem;
  }
`;

const FoulCell = styled.td`
  width: 80px;

  @media (max-width: 768px) {
    width: 50px;
  }
`;

const NominationCell = styled.td`
  width: 120px;
  text-align: center;

  @media (max-width: 768px) {
    width: 70px;
  }
`;

const MutedText = styled.span`
  opacity: 0.4;
  font-size: 0.85rem;
`;

const PlayerInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const PlayerAvatar = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 12px;
  object-fit: cover;
  border: 2px solid rgba(255, 255, 255, 0.12);

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    border-radius: 10px;
  }
`;

const PlayerName = styled.span`
  font-weight: 600;
  font-size: 1rem;

  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const FoulBadge = styled.button.attrs({ type: "button" })`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: #f5f2ff;
  font-weight: 700;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.18);
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    font-size: 0.85rem;
  }
`;

const NominationButton = styled.button.attrs({ type: "button" })<{
  $active?: boolean;
}>`
  min-width: 72px;
  padding: 0.35rem 0.9rem;
  border-radius: 16px;
  border: 1px solid
    ${({ $active }) =>
      $active ? "rgba(68, 255, 210, 0.45)" : "rgba(255, 255, 255, 0.18)"};
  background: ${({ $active }) =>
    $active
      ? "linear-gradient(135deg, rgba(68, 255, 210, 0.35), rgba(124, 93, 255, 0.55))"
      : "rgba(255, 255, 255, 0.04)"};
  color: ${({ $active }) =>
    $active ? "#f5f2ff" : "rgba(245, 242, 255, 0.85)"};
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 12px 32px rgba(124, 93, 255, 0.32);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    box-shadow: none;
  }

  @media (max-width: 768px) {
    min-width: 56px;
    padding: 0.4rem 0.6rem;
    font-size: 0.65rem;
    border-radius: 12px;
  }
`;

const buttonVariants = {
  primary: css`
    background: linear-gradient(
      135deg,
      rgba(124, 93, 255, 0.95),
      rgba(70, 46, 150, 0.95)
    );
    border: 1px solid rgba(124, 93, 255, 0.55);
  `,
  secondary: css`
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.18);
  `,
  danger: css`
    background: linear-gradient(
      135deg,
      rgba(255, 96, 122, 0.95),
      rgba(214, 38, 78, 0.95)
    );
    border: 1px solid rgba(255, 96, 122, 0.5);
  `,
  outline: css`
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.22);
    color: #f5f2ff;

    &:hover {
      background: rgba(255, 255, 255, 0.08);
    }
  `,
  ghost: css`
    background: transparent;
    border: 1px solid transparent;
    color: rgba(245, 242, 255, 0.65);

    &:hover {
      color: #f5f2ff;
      background: rgba(255, 255, 255, 0.08);
    }
  `,
} as const;

type ButtonVariant = keyof typeof buttonVariants;

const PhaseButton = styled.button.attrs({ type: "button" })<{
  $variant?: ButtonVariant;
}>`
  border-radius: 16px;
  padding: 0.75rem 1.25rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: #f5f2ff;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease,
    color 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  ${({ $variant = "primary" }) => buttonVariants[$variant]};

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 14px 36px rgba(124, 93, 255, 0.32);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const ActionBar = styled.div`
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
`;

const MetaInfo = styled.div`
  text-align: center;
  font-size: 0.85rem;
  line-height: 1.6;
  color: inherit;
`;

const MetaHighlight = styled.span`
  color: #f5f2ff;
  font-weight: 600;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(5, 3, 12, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  z-index: 1050;
`;

const ModalCard = styled.div`
  width: min(520px, 100%);
  background: linear-gradient(
    145deg,
    rgba(22, 14, 42, 0.96),
    rgba(12, 8, 28, 0.96)
  );
  border-radius: 26px;
  border: 1px solid rgba(124, 93, 255, 0.32);
  box-shadow: 0 28px 90px rgba(4, 2, 14, 0.65);
  color: #f5f2ff;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 1.75rem 0;
`;

const ModalTitle = styled.h5`
  margin: 0;
  font-size: 1.1rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #dcd4ff;
`;

const ModalCloseButton = styled.button.attrs({ type: "button" })`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.16);
  color: #f5f2ff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.18);
    transform: rotate(90deg);
  }
`;

const ModalBody = styled.div`
  padding: 1.25rem 1.75rem 1.5rem;
`;

const ModalPlayer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const ModalAvatar = styled.img`
  width: 64px;
  height: 64px;
  border-radius: 18px;
  object-fit: cover;
  border: 2px solid rgba(124, 93, 255, 0.4);
`;

const ModalPlayerInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;

const ModalPlayerName = styled.span`
  font-weight: 700;
  font-size: 1.1rem;
`;

const ModalNote = styled.span`
  font-size: 0.85rem;
  opacity: 0.68;
`;

const NominationGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
`;

const NominationOption = styled.button.attrs({ type: "button" })<{
  $active?: boolean;
}>`
  width: 52px;
  height: 52px;
  border-radius: 16px;
  font-weight: 700;
  font-size: 1rem;
  border: 1px solid
    ${({ $active }) =>
      $active ? "rgba(68, 255, 210, 0.65)" : "rgba(255, 255, 255, 0.18)"};
  background: ${({ $active }) =>
    $active
      ? "linear-gradient(135deg, rgba(68, 255, 210, 0.35), rgba(124, 93, 255, 0.55))"
      : "rgba(255, 255, 255, 0.05)"};
  color: #f5f2ff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.35;
      cursor: not-allowed;
      border-color: rgba(255, 255, 255, 0.12);
    `}

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 12px 32px rgba(68, 255, 210, 0.3);
  }
`;

const ModalInfo = styled.div`
  margin-top: 1.25rem;
  padding: 0.9rem 1.1rem;
  border-radius: 18px;
  background: rgba(68, 255, 210, 0.12);
  border: 1px solid rgba(68, 255, 210, 0.35);
  text-align: center;
  color: #b8ffef;
  font-weight: 600;
`;

const ModalFooter = styled.div`
  padding: 0 1.75rem 1.75rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: flex-end;
`;

const SettingsGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const SettingsLabel = styled.label`
  font-weight: 600;
  font-size: 0.9rem;
  color: #dcd4ff;
  margin-bottom: 0.5rem;
`;

const SettingsSelect = styled.select`
  padding: 0.75rem 1rem;
  border-radius: 16px;
  border: 1px solid rgba(124, 93, 255, 0.32);
  background: rgba(255, 255, 255, 0.05);
  color: #f5f2ff;
  font-weight: 500;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(124, 93, 255, 0.65);
  }

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }

  option {
    background: rgba(18, 12, 36, 0.98);
    color: #f5f2ff;
  }
`;

const STREAM_THEMES = [
  { value: 'classic', label: 'theme_classic' },
  { value: 'dark', label: 'theme_dark' },
  { value: 'noroles', label: 'theme_noroles' },
  { value: 'cartel', label: 'theme_cartel' },
  { value: 'x-city', label: 'theme_xcity' },
];

const STREAM_LANGUAGES = [
  { value: 0, label: 'lang_english' },
  { value: 1, label: 'lang_russian' },
  { value: 2, label: 'lang_ukrainian' },
  { value: 3, label: 'lang_georgian' },
  { value: 4, label: 'lang_hebrew' },
];

const StreamLinkBox = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  border-radius: 12px;
  background: rgba(124, 93, 255, 0.08);
  border: 1px solid rgba(124, 93, 255, 0.22);

  b {
    display: block;
    margin-bottom: 0.75rem;
    color: #f5f2ff;
  }
`;

const StreamLinkContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const StreamLinkText = styled.span`
  font-size: 0.9rem;
  word-break: break-all;
  color: #f5f2ff;
  flex: 1;
  min-width: 200px;
`;

const CopyButton = styled.button.attrs({ type: "button" })`
  padding: 0.5rem 1rem;
  border-radius: 12px;
  border: 1px solid rgba(124, 93, 255, 0.3);
  background: rgba(255, 255, 255, 0.08);
  color: #f5f2ff;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: rgba(124, 93, 255, 0.18);
    border-color: rgba(124, 93, 255, 0.5);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const DayPhase: React.FC<DayPhaseProps> = ({
  language,
  players,
  playerRoles,
  judge,
  playersLeaved,
  playersLeavingNow = [],
  playersLeavedGP,
  playersLeavedPenalty,
  playerFauls,
  penaltyListToday,
  firstTalk,
  nightCount,
  handledFouls,
  nominations,
  eliminatedPlayers,
  eliminatedPlayerNumbers,
  eliminationType,
  votingInProgress = false,
  streamError = false,
  morningTransition,
  streamLink,
  getOrderedPlayersFromFirstTalk,
  onNominationChange,
  onSaveDayState,
  onVotingStart,
  onVotingComplete,
  onSetVotingInProgress,
  onGoToNight,
  onPlayerPunish,
  onPlayerFoulAction,
  onMarkFoulAsHandled,
  onDisqualifyPlayer,
  onPPK,
  onLastWordsComplete,
  onLastWordsUndo,
  onLanguageChange,
  onStreamSettingsChange,
  onTimeoutToggle,
  onTechnicalBreak,
  onTechnicalBreakEnd,
  onCancel,
  timerAccumulated,
  timerStartedAt,
  onTimerStateChange,
}) => {
  const [hasNominations, setHasNominations] = useState(false);
  const [showGameActions, setShowGameActions] = useState(false);
  const [showPlayerPunishment, setShowPlayerPunishment] = useState<
    number | null
  >(null);
  const [showVoting, setShowVoting] = useState(false);
  const votingSectionRef = useRef<HTMLDivElement | null>(null);
  const [showNominationModal, setShowNominationModal] = useState<number | null>(
    null
  );
  const [showDisqualificationWarning, setShowDisqualificationWarning] = useState(false);
  const [showCancelGameConfirm, setShowCancelGameConfirm] = useState(false);
  const [showGoNightConfirm, setShowGoNightConfirm] = useState(false);
  const [pendingFoulDialogs, setPendingFoulDialogs] = useState<
    Array<{ playerIndex: number; foulCount: number }>
  >([]);
  const processingFoulRef = useRef(false);
  const [showSettings, setShowSettings] = useState(false);
  
  // Current saved settings (loaded from localStorage)
  const [currentStreamTheme, setCurrentStreamTheme] = useState<string>(() => {
    return localStorage.getItem('streamTheme') || 'dark';
  });
  const [currentStreamLanguage, setCurrentStreamLanguage] = useState<number>(() => {
    const saved = localStorage.getItem('streamLanguage');
    return saved !== null ? parseInt(saved) : 1; // Default to Russian (1)
  });
  
  // Temporary settings for modal (before saving)
  const [tempAppLanguage, setTempAppLanguage] = useState<number>(language);
  const [tempStreamTheme, setTempStreamTheme] = useState<string>(currentStreamTheme);
  const [tempStreamLanguage, setTempStreamLanguage] = useState<number>(currentStreamLanguage);

  const dict = dictionaries[language].ph;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (!showVoting) {
      return;
    }

    const scrollToVoting = () => {
      if (votingSectionRef.current) {
        votingSectionRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    };

    if (typeof window !== "undefined" && "requestAnimationFrame" in window) {
      window.requestAnimationFrame(scrollToVoting);
    } else {
      scrollToVoting();
    }
  }, [showVoting]);

  useEffect(() => {
    if (showPlayerPunishment !== null || pendingFoulDialogs.length === 0) {
      return;
    }

    const nextDialog = pendingFoulDialogs[0];
    setShowPlayerPunishment(nextDialog.playerIndex);
    onMarkFoulAsHandled(nextDialog.playerIndex, nextDialog.foulCount);
    setPendingFoulDialogs((prev) => prev.slice(1));
  }, [showPlayerPunishment, pendingFoulDialogs, onMarkFoulAsHandled]);

  useEffect(() => {
    for (let index = 0; index < playerFauls.length; index++) {
      const fouls = playerFauls[index];

      if (fouls !== 3 && fouls !== 4) continue;

      const foulKey = `${index}-${fouls}`;

      if (!handledFouls.has(foulKey)) {
        setPendingFoulDialogs((prev) => {
          const alreadyQueued = prev.some(
            (dialog) =>
              dialog.playerIndex === index && dialog.foulCount === fouls
          );

          if (!alreadyQueued) {
            return [...prev, { playerIndex: index, foulCount: fouls }];
          }
          return prev;
        });
      }
    }
  }, [playerFauls, handledFouls]);

  useEffect(() => {
    const hasAnyNominations = Object.values(nominations).some(
      (nom) => nom !== ""
    );
    setHasNominations(hasAnyNominations);
  }, [nominations]);

  const handleNominationChange = (playerIndex: number, nomination: string) => {
    onNominationChange(playerIndex, nomination);
  };

  const getPlayerClass = (playerIndex: number) => {
    const playerNumber = playerIndex + 1;
    let classes = "";

    if (playersLeaved.includes(playerNumber)) {
      classes += " dead";
    }

    const isPenaltyToday = penaltyListToday.includes(playerNumber);

    if (firstTalk === playerIndex && !playersLeaved.includes(playerNumber)) {
      if (isPenaltyToday) {
        classes += " penaltytalk";
      } else {
        classes += " talk";
      }
    } else if (isPenaltyToday && !playersLeaved.includes(playerNumber)) {
      classes += " penalty";
    }

    return classes.trim();
  };

  const isPlayerAlive = (playerIndex: number) => {
    const playerNumber = playerIndex + 1;
    // Player is alive if they're not in playersLeaved
    // Players in playersLeavingNow are still shown (they're saying farewell)
    return !playersLeaved.includes(playerNumber);
  };

  // Check if there was a disqualification in current or previous phase
  const hasRecentDisqualification = () => {
    if (playersLeavedPenalty.length === 0) {
      return false;
    }

    // Current phase number (even = day, odd = night)
    const currentPhase = nightCount * 2; // Current day phase
    const previousPhase = currentPhase - 1; // Previous night phase

    // Check if any disqualified player was disqualified in current or previous phase
    for (let i = 0; i < playersLeavedPenalty.length; i++) {
      const disqualifiedPlayer = playersLeavedPenalty[i];
      const playerIndex = playersLeaved.indexOf(disqualifiedPlayer);
      
      if (playerIndex !== -1) {
        const eliminationPhase = playersLeavedGP[playerIndex];
        
        // Check current phase
        if (eliminationPhase === currentPhase) {
          return true;
        }
        
        // For previous phase (night), only count if multiple players left that phase
        if (eliminationPhase === previousPhase) {
          // Count how many players left in the previous phase
          const playersLeftInPreviousPhase = playersLeavedGP.filter(
            phase => phase === previousPhase
          ).length;
          
          // If player was the only one removed in previous phase (likely killed then removed),
          // don't count them
          if (playersLeftInPreviousPhase > 1) {
            return true;
          }
        }
      }
    }

    return false;
  };

  const handleStartVoting = () => {
    if (hasNominations && !votingInProgress) {
      // Check if there was a recent disqualification
      if (hasRecentDisqualification()) {
        setShowDisqualificationWarning(true);
      } else {
        proceedWithVoting();
      }
    }
  };

  const proceedWithVoting = () => {
    onSaveDayState();
    if (onSetVotingInProgress) {
      onSetVotingInProgress(true); // Set voting in progress flag
    }
    setShowVoting(true);
    // Scroll to top immediately
    window.scrollTo(0, 0);
  };

  const handleVotingComplete = (results: any) => {
    setShowVoting(false);

    if (onVotingComplete) {
      onVotingComplete(results);
    } else {
      onVotingStart(nominations);
    }
  };

  const handleVotingCancel = () => {
    setShowVoting(false);
    if (onSetVotingInProgress) {
      onSetVotingInProgress(false); // Reset voting in progress flag on cancel
    }
  };

  const handleNominationModalOpen = (playerIndex: number) => {
    setShowNominationModal(playerIndex);
  };

  const handleNominationModalClose = () => {
    setShowNominationModal(null);
  };

  const handleNominationSelect = (
    nominatorIndex: number,
    targetNumber: string
  ) => {
    handleNominationChange(nominatorIndex, targetNumber);
    setShowNominationModal(null);
  };

  const alreadyNominatedPlayers = useMemo(
    () =>
      Object.values(nominations)
        .map((nomination) => parseInt(nomination, 10))
        .filter((num) => !isNaN(num)),
    [nominations]
  );

  const handleGoToNight = () => {
    // Check if there was a recent disqualification - if yes, skip confirmation
    if (hasRecentDisqualification()) {
      onGoToNight();
    } else {
      // Show confirmation dialog
      setShowGoNightConfirm(true);
    }
  };

  const handleCancelGame = () => {
    setShowCancelGameConfirm(true);
  };

  const handleOpenSettings = () => {
    // Initialize temp values with current values
    setTempAppLanguage(language);
    setTempStreamTheme(currentStreamTheme);
    setTempStreamLanguage(currentStreamLanguage);
    setShowSettings(true);
  };

  const handleCloseSettings = () => {
    // Reset temp values to current values (cancel changes)
    setTempAppLanguage(language);
    setTempStreamTheme(currentStreamTheme);
    setTempStreamLanguage(currentStreamLanguage);
    setShowSettings(false);
  };

  const handleSaveSettings = () => {
    // Apply app language change
    if (tempAppLanguage !== language && onLanguageChange) {
      onLanguageChange(tempAppLanguage);
    }

    // Apply stream settings changes
    const streamSettingsChanged = 
      tempStreamTheme !== currentStreamTheme || 
      tempStreamLanguage !== currentStreamLanguage;
    
    if (streamSettingsChanged) {
      // Update current values
      setCurrentStreamTheme(tempStreamTheme);
      setCurrentStreamLanguage(tempStreamLanguage);
      
      // Save to localStorage
      localStorage.setItem('streamTheme', tempStreamTheme);
      localStorage.setItem('streamLanguage', tempStreamLanguage.toString());
      
      // Notify parent component
      if (onStreamSettingsChange) {
        onStreamSettingsChange(tempStreamTheme, tempStreamLanguage);
      }
    }
    
    setShowSettings(false);
  };

  const handlePlayerPunish = (playerIndex: number) => {
    if (processingFoulRef.current) {
      return;
    }

    processingFoulRef.current = true;

    // Just call the parent handler - the useEffect will detect changes and show modal
    onPlayerPunish(playerIndex);

    // Reset after a minimal delay to allow React to process the state update
    // This prevents double-clicks but doesn't block rapid consecutive foul additions
    setTimeout(() => {
      processingFoulRef.current = false;
    }, 100);
  };

  const handlePlayerFoulAction = (
    action:
      | "add"
      | "reset"
      | "disqualify"
      | "penalty-today"
      | "penalty-tomorrow"
  ) => {
    if (showPlayerPunishment !== null) {
      onPlayerFoulAction(showPlayerPunishment, action);
      setShowPlayerPunishment(null);
    }
  };

  const renderPlayerRow = (player: Player, index: number) => {
    const playerNumber = index + 1;
    const isAlive = isPlayerAlive(index);
    const currentNomination = nominations[index.toString()] || "";

    return (
      <PlayerRow
        key={player.user_id}
        id={`player${player.user_id}`}
        className={getPlayerClass(index)}
      >
        <NumberCell>{playerNumber}</NumberCell>
        <FoulCell>
          <FoulBadge onClick={() => handlePlayerPunish(index)}>
            {playerFauls[index] > 0 ? playerFauls[index] : "-"}
          </FoulBadge>
        </FoulCell>
        <td>
          <PlayerInfo>
            <PlayerAvatar src={player.avatar} alt={player.nickname} />
            <PlayerName>{player.nickname}</PlayerName>
          </PlayerInfo>
        </td>
        <NominationCell>
          {isAlive ? (
            <NominationButton
              $active={!!currentNomination}
              onClick={() => handleNominationModalOpen(index)}
            >
              {currentNomination ? currentNomination : dict.vs}
            </NominationButton>
          ) : (
            <MutedText>-</MutedText>
          )}
        </NominationCell>
      </PlayerRow>
    );
  };

  const orderedPlayers = getOrderedPlayersFromFirstTalk(
    firstTalk,
    playersLeaved
  );

  return (
    <PhaseBackground>
      <PhaseContainer>
        <PhaseHeader
          title={dict.dph}
          variant="day"
          language={language}
          meta={`#${String(nightCount === 0 ? 1 : nightCount + 1).padStart(
            2,
            "0"
          )}`}
          streamError={streamError}
          onTimeoutToggle={onTimeoutToggle}
          timerAccumulated={timerAccumulated}
          timerStartedAt={timerStartedAt}
          onTimerStateChange={onTimerStateChange}
          onTechnicalBreak={onTechnicalBreak}
          onTechnicalBreakEnd={onTechnicalBreakEnd}
          actions={[
            {
              id: "voting",
              label: dict.voting,
              onClick: handleStartVoting,
              disabled: !hasNominations || votingInProgress,
              variant: "primary",
            },
            {
              id: "go-night",
              label: dict.gngt,
              onClick: handleGoToNight,
              variant: "secondary",
            },
          ]}
        />

        <PhaseMain>
          <PhaseStack>
            {morningTransition && (
              <SectionCard>
                <MorningTransition
                  language={language}
                  players={players}
                  playerRoles={playerRoles}
                  playerFauls={playerFauls}
                  eliminatedPlayer={morningTransition.eliminatedPlayer}
                  nightCount={nightCount}
                  isFirstNight={morningTransition.isFirstNight}
                  firstDayEliminations={morningTransition.firstDayEliminations}
                  onBestMove={morningTransition.onBestMove}
                  onLastWords={morningTransition.onLastWords}
                  onContinue={morningTransition.onContinue}
                  onUndo={morningTransition.onUndo}
                />
              </SectionCard>
            )}

            {eliminatedPlayers &&
              eliminatedPlayers.length > 0 &&
              onLastWordsComplete && (
                <SectionCard>
                  <LastWords
                    language={language}
                    eliminatedPlayers={eliminatedPlayers}
                    eliminatedPlayerNumbers={eliminatedPlayerNumbers || []}
                    eliminationType={eliminationType || "voted"}
                    playerFauls={playerFauls}
                    playerRoles={playerRoles}
                    onComplete={onLastWordsComplete}
                    onUndo={onLastWordsUndo}
                  />
                </SectionCard>
              )}

            {showVoting && (
              <VotingSectionCard ref={votingSectionRef}>
                <Voting
                  language={language}
                  players={players}
                  playersLeaved={playersLeaved}
                  nominations={nominations}
                  nightCount={nightCount}
                  firstTalk={firstTalk}
                  getOrderedPlayersFromFirstTalk={
                    getOrderedPlayersFromFirstTalk
                  }
                  onVotingComplete={handleVotingComplete}
                  onCancel={handleVotingCancel}
                />
              </VotingSectionCard>
            )}

            <PlayersTable>
              <thead>
                <tr>
                  <th>#</th>
                  <th>{dict.fls}</th>
                  <th>{dict.player}</th>
                  <th>{dict.vs}</th>
                </tr>
              </thead>
              <tbody>
                {players.map((player, index) => {
                  if (!isPlayerAlive(index)) return null;
                  return renderPlayerRow(player, index);
                })}
              </tbody>
            </PlayersTable>

            <InstructionCard>
              <strong>{dict.day_instructions_title}</strong>
              <ul className="mb-0">
                <li>{dict.day_instruction_discuss}</li>
                <li>{dict.day_instruction_vs}</li>
                <li>{dict.day_instruction_disabled}</li>
                <li>{dict.day_instruction_fouls}</li>
                <li>{dict.day_instruction_voting}</li>
                <li>{dict.day_instruction_skip}</li>
              </ul>
            </InstructionCard>

            {hasNominations && (
              <NominationSummaryCard>
                <strong>{dict.current_nominations_title}</strong>
                <ul className="mb-0">
                  {(() => {
                    // Get speaking order starting from firstTalk
                    const speakingOrder = getOrderedPlayersFromFirstTalk(
                      firstTalk,
                      playersLeaved
                    );
                    
                    // Sort nominations by speaking order
                    return speakingOrder
                      .map((playerNumber) => {
                        const playerIndex = playerNumber - 1;
                        const nomination = nominations[playerIndex.toString()];
                        
                        if (!nomination) {
                          return null;
                        }

                        const nominator = players[playerIndex];
                        const nominated = players[parseInt(nomination, 10) - 1];
                        const nominatedName =
                          nominated?.nickname ||
                          `${dict.player} ${nomination}`;

                        return (
                          <li key={playerIndex}>
                            {dict.nomination_item
                              .replace('{0}', nominator.nickname)
                              .replace('{1}', nominatedName)}
                          </li>
                        );
                      })
                      .filter((item) => item !== null);
                  })()}
                </ul>
              </NominationSummaryCard>
            )}

            <SectionCard>
              <ActionBar>
                <PhaseButton
                  $variant="secondary"
                  onClick={() => setShowGameActions(true)}
                >
                  ⚖️ {dict.actions}
                </PhaseButton>
                <PhaseButton
                  $variant="secondary"
                  onClick={handleOpenSettings}
                >
                  ⚙️ {dict.settings_button}
                </PhaseButton>
                <PhaseButton $variant="danger" onClick={handleCancelGame}>
                  {dict.cancelgame}
                </PhaseButton>
              </ActionBar>
            </SectionCard>

            <MetaCard>
              <MetaInfo>
                {nightCount === 0 ? dict.first_day : dict.day_number.replace('{0}', String(nightCount + 1))} •
                {dict.first_speaker}{" "}
                <MetaHighlight>
                  {players[firstTalk]?.nickname || dict.unknown} ({dict.player}{" "}
                  {firstTalk + 1})
                </MetaHighlight>
              </MetaInfo>
              <MetaInfo>
                {dict.nomination_order.replace(
                  '{0}',
                  orderedPlayers.join(" → ")
                )}
              </MetaInfo>
            </MetaCard>
          </PhaseStack>
        </PhaseMain>

        {showPlayerPunishment !== null && (
          <PlayerPunishment
            language={language}
            player={players[showPlayerPunishment]}
            playerIndex={showPlayerPunishment}
            currentFouls={playerFauls[showPlayerPunishment] || 0}
            onFoulAction={handlePlayerFoulAction}
          />
        )}

        {showGameActions && (
          <GameActions
            language={language}
            players={players}
            playerRoles={playerRoles}
            playersLeaved={playersLeaved}
            onDisqualifyPlayer={onDisqualifyPlayer}
            onPPK={onPPK}
            onClose={() => setShowGameActions(false)}
          />
        )}

        {showDisqualificationWarning && (
          <ModalOverlay>
            <ModalCard>
              <ModalHeader>
                <ModalTitle>⚠️ {dict.disqualification_warning_title}</ModalTitle>
                <ModalCloseButton onClick={() => setShowDisqualificationWarning(false)}>
                  ×
                </ModalCloseButton>
              </ModalHeader>

              <ModalBody>
                <ModalInfo>
                  {dict.disqualification_warning_message}
                </ModalInfo>
              </ModalBody>

              <ModalFooter>
                <PhaseButton 
                  $variant="secondary" 
                  onClick={() => {
                    setShowDisqualificationWarning(false);
                    proceedWithVoting();
                  }}
                >
                  {dict.vote_anyway}
                </PhaseButton>
                <PhaseButton 
                  $variant="primary" 
                  onClick={() => {
                    setShowDisqualificationWarning(false);
                    onGoToNight();
                  }}
                >
                  {dict.skip_voting_go_night}
                </PhaseButton>
              </ModalFooter>
            </ModalCard>
          </ModalOverlay>
        )}

        {showSettings && (
          <ModalOverlay>
            <ModalCard>
              <ModalHeader>
                <ModalTitle>{dict.settings_button}</ModalTitle>
                <ModalCloseButton onClick={handleCloseSettings}>
                  ×
                </ModalCloseButton>
              </ModalHeader>
              <ModalBody>
                <SettingsGroup>
                  <SettingsLabel>{dict.app_language_label}</SettingsLabel>
                  <SettingsSelect
                    value={tempAppLanguage}
                    onChange={(e) => setTempAppLanguage(parseInt(e.target.value))}
                  >
                    {STREAM_LANGUAGES.map((lang) => (
                      <option key={lang.value} value={lang.value}>
                        {dict[lang.label]}
                      </option>
                    ))}
                  </SettingsSelect>
                </SettingsGroup>

                <SettingsGroup>
                  <SettingsLabel>{dict.stream_language_label}</SettingsLabel>
                  <SettingsSelect
                    value={tempStreamLanguage}
                    onChange={(e) => setTempStreamLanguage(parseInt(e.target.value))}
                  >
                    {STREAM_LANGUAGES.map((lang) => (
                      <option key={lang.value} value={lang.value}>
                        {dict[lang.label]}
                      </option>
                    ))}
                  </SettingsSelect>
                </SettingsGroup>

                <SettingsGroup>
                  <SettingsLabel>{dict.stream_theme_label}</SettingsLabel>
                  <SettingsSelect
                    value={tempStreamTheme}
                    onChange={(e) => setTempStreamTheme(e.target.value)}
                  >
                    {STREAM_THEMES.map((theme) => (
                      <option key={theme.value} value={theme.value}>
                        {dict[theme.label]}
                      </option>
                    ))}
                  </SettingsSelect>
                </SettingsGroup>

                {/* Stream Link */}
                {streamLink && (
                  <StreamLinkBox>
                    <StreamLinkContent>
                      <StreamLinkText>{streamLink}</StreamLinkText>
                      <CopyButton
                        onClick={() => {
                          navigator.clipboard.writeText(streamLink).catch(err => 
                            console.error("Failed to copy:", err)
                          );
                        }}
                      >
                        📋 {dict.copylink || "Copy"}
                      </CopyButton>
                    </StreamLinkContent>
                  </StreamLinkBox>
                )}

              </ModalBody>
              <ModalFooter>
                <PhaseButton $variant="secondary" onClick={handleCloseSettings}>
                  {dict.close}
                </PhaseButton>
                <PhaseButton $variant="primary" onClick={handleSaveSettings}>
                  {dict.save}
                </PhaseButton>
              </ModalFooter>
            </ModalCard>
          </ModalOverlay>
        )}

        {showNominationModal !== null && (
          <ModalOverlay>
            <ModalCard>
              <ModalHeader>
                <ModalTitle>
                  {dict.nomination_title.replace(
                    '{0}',
                    String(showNominationModal + 1)
                  )}
                </ModalTitle>
                <ModalCloseButton onClick={handleNominationModalClose}>
                  ×
                </ModalCloseButton>
              </ModalHeader>
              <ModalBody>
                <ModalPlayer>
                  <ModalAvatar
                    src={players[showNominationModal].avatar}
                    alt={players[showNominationModal].nickname}
                  />
                  <ModalPlayerInfo>
                    <ModalPlayerName>
                      {players[showNominationModal].nickname}
                    </ModalPlayerName>
                    <ModalNote>
                      {dict.nomination_prompt}
                    </ModalNote>
                  </ModalPlayerInfo>
                </ModalPlayer>

                <NominationGrid>
                  {players.map((_, index) => {
                    const targetNumber = index + 1;
                    const isTargetAlive = isPlayerAlive(index);
                    const isAlreadyNominated =
                      alreadyNominatedPlayers.includes(targetNumber);
                    const currentNomination =
                      nominations[showNominationModal.toString()] || "";
                    const isCurrentSelection =
                      currentNomination === targetNumber.toString();
                    const isDisabled =
                      !isTargetAlive ||
                      (isAlreadyNominated && !isCurrentSelection);

                    return (
                      <NominationOption
                        key={targetNumber}
                        $active={isCurrentSelection}
                        disabled={isDisabled}
                        onClick={() =>
                          handleNominationSelect(
                            showNominationModal,
                            targetNumber.toString()
                          )
                        }
                        title={
                          !isTargetAlive
                            ? dict.player_eliminated_tooltip.replace(
                                '{0}',
                                String(targetNumber)
                              )
                            : isAlreadyNominated && !isCurrentSelection
                            ? dict.player_already_nominated.replace(
                                '{0}',
                                String(targetNumber)
                              )
                            : dict.nominate_player.replace(
                                '{0}',
                                String(targetNumber)
                              )
                        }
                      >
                        {targetNumber}
                      </NominationOption>
                    );
                  })}
                </NominationGrid>

                {nominations[showNominationModal.toString()] && (
                  <ModalInfo>
                    {dict.current_nomination.replace(
                      '{0}',
                      nominations[showNominationModal.toString()]
                    )}
                  </ModalInfo>
                )}
              </ModalBody>
              <ModalFooter>
                {nominations[showNominationModal.toString()] && (
                  <PhaseButton
                    $variant="danger"
                    onClick={() =>
                      handleNominationSelect(showNominationModal, "")
                    }
                  >
                    {dict.clear_nomination}
                  </PhaseButton>
                )}
                <PhaseButton
                  $variant="outline"
                  onClick={handleNominationModalClose}
                >
                  {dict.close}
                </PhaseButton>
              </ModalFooter>
            </ModalCard>
          </ModalOverlay>
        )}

        {showGoNightConfirm && (
          <ModalOverlay>
            <ModalCard>
              <ModalHeader>
                <ModalTitle>⚠️ {dict.confirm_go_night}</ModalTitle>
                <ModalCloseButton onClick={() => setShowGoNightConfirm(false)}>
                  ×
                </ModalCloseButton>
              </ModalHeader>

              <ModalBody>
                <ModalInfo>
                  {dict.confirm_go_night_message}
                </ModalInfo>
              </ModalBody>

              <ModalFooter>
                <PhaseButton 
                  $variant="secondary" 
                  onClick={() => setShowGoNightConfirm(false)}
                >
                  {dict.cncl}
                </PhaseButton>
                <PhaseButton 
                  $variant="primary" 
                  onClick={() => {
                    setShowGoNightConfirm(false);
                    onGoToNight();
                  }}
                >
                  {dict.yes_proceed}
                </PhaseButton>
              </ModalFooter>
            </ModalCard>
          </ModalOverlay>
        )}

        {showCancelGameConfirm && (
          <ModalOverlay>
            <ModalCard>
              <ModalHeader>
                <ModalTitle>⚠️ {dict.confirm_cancel_game}</ModalTitle>
                <ModalCloseButton onClick={() => setShowCancelGameConfirm(false)}>
                  ×
                </ModalCloseButton>
              </ModalHeader>

              <ModalBody>
                <ModalInfo>
                  {dict.confirm_cancel_game_message}
                </ModalInfo>
              </ModalBody>

              <ModalFooter>
                <PhaseButton 
                  $variant="secondary" 
                  onClick={() => setShowCancelGameConfirm(false)}
                >
                  {dict.cncl}
                </PhaseButton>
                <PhaseButton 
                  $variant="danger" 
                  onClick={() => {
                    setShowCancelGameConfirm(false);
                    onCancel();
                  }}
                >
                  {dict.yes_cancel}
                </PhaseButton>
              </ModalFooter>
            </ModalCard>
          </ModalOverlay>
        )}
      </PhaseContainer>
    </PhaseBackground>
  );
};

export default DayPhase;
